import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { vehiculoAPI, entidadAPI, personaAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Upload, Truck, Building, Users, FileText, Loader2, Download, CheckCircle } from 'lucide-react';

export default function CargaMasivaPage() {
    const { user } = useAuth();
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState('vehiculos');
    const [results, setResults] = useState(null);
    
    const vehiculosInputRef = useRef(null);
    const entidadesInputRef = useRef(null);
    const personasInputRef = useRef(null);

    const handleUpload = async (type, file) => {
        if (!file) return;
        
        setUploading(true);
        setResults(null);
        
        try {
            let response;
            switch (type) {
                case 'vehiculos':
                    response = await vehiculoAPI.upload(file);
                    break;
                case 'entidades':
                    response = await entidadAPI.upload(file);
                    break;
                case 'personas':
                    response = await personaAPI.upload(file);
                    break;
                default:
                    return;
            }
            
            setResults({ success: true, message: response.data.message });
            toast.success('Carga completada', { description: response.data.message });
        } catch (err) {
            const message = err.response?.data?.detail || 'Error al procesar el archivo';
            setResults({ success: false, message });
            toast.error('Error en la carga', { description: message });
        } finally {
            setUploading(false);
        }
    };

    const downloadTemplate = (type) => {
        let csvContent = '';
        let filename = '';
        
        switch (type) {
            case 'vehiculos':
                csvContent = 'patente,marca,modelo\nABCD12,Volvo,FH16\nWXYZ98,Mercedes,Actros';
                filename = 'plantilla_vehiculos.csv';
                break;
            case 'entidades':
                csvContent = 'rut,razon_social\n76.123.456-7,Empresa Ejemplo S.A.\n77.888.999-K,Servicios Ltda.';
                filename = 'plantilla_entidades.csv';
                break;
            case 'personas':
                csvContent = 'rut,nombre,empresa_vinculada\n12.345.678-9,Juan Pérez,Empresa Ejemplo S.A.\n98.765.432-1,María López,';
                filename = 'plantilla_personas.csv';
                break;
            default:
                return;
        }
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
    };

    const UploadSection = ({ type, icon: Icon, title, description, inputRef }) => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-3 rounded-sm bg-primary/10">
                        <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                        <h3 className="font-heading font-bold uppercase">{title}</h3>
                        <p className="text-sm text-muted-foreground">{description}</p>
                    </div>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadTemplate(type)}
                    className="border-2"
                    data-testid={`download-template-${type}`}
                >
                    <Download className="h-4 w-4 mr-2" />
                    Plantilla
                </Button>
            </div>

            <div 
                className="border-2 border-dashed border-muted-foreground/30 rounded-sm p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => inputRef.current?.click()}
                data-testid={`upload-zone-${type}`}
            >
                <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
                <p className="font-medium mb-1">Haz clic para seleccionar archivo</p>
                <p className="text-sm text-muted-foreground">CSV o Excel (.csv, .xlsx)</p>
                <Input
                    ref={inputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    className="hidden"
                    onChange={(e) => handleUpload(type, e.target.files?.[0])}
                    data-testid={`file-input-${type}`}
                />
            </div>

            {uploading && activeTab === type && (
                <div className="flex items-center justify-center gap-2 p-4 bg-muted rounded-sm">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Procesando archivo...</span>
                </div>
            )}

            {results && activeTab === type && (
                <div className={`p-4 rounded-sm border-2 ${results.success ? 'bg-success/10 border-success/30' : 'bg-destructive/10 border-destructive/30'}`}>
                    <div className="flex items-center gap-2">
                        {results.success ? (
                            <CheckCircle className="h-5 w-5 text-success" />
                        ) : (
                            <FileText className="h-5 w-5 text-destructive" />
                        )}
                        <span className={results.success ? 'text-success' : 'text-destructive'}>
                            {results.message}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <Layout>
            <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
                {/* Header */}
                <div>
                    <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                        Carga Masiva
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Importe datos desde archivos CSV para alimentar las tablas maestras
                    </p>
                </div>

                <Card className="border-2">
                    <CardHeader className="card-industrial-header">
                        <CardTitle>Tablas Maestras</CardTitle>
                        <CardDescription>
                            Seleccione el tipo de datos a importar
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="p-6">
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList className="grid w-full grid-cols-3 mb-6">
                                <TabsTrigger value="vehiculos" className="uppercase text-xs tracking-wider font-bold">
                                    <Truck className="h-4 w-4 mr-2" />
                                    Vehículos
                                </TabsTrigger>
                                <TabsTrigger value="entidades" className="uppercase text-xs tracking-wider font-bold">
                                    <Building className="h-4 w-4 mr-2" />
                                    Entidades
                                </TabsTrigger>
                                <TabsTrigger value="personas" className="uppercase text-xs tracking-wider font-bold">
                                    <Users className="h-4 w-4 mr-2" />
                                    Personas
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="vehiculos">
                                <UploadSection
                                    type="vehiculos"
                                    icon={Truck}
                                    title="Vehículos"
                                    description="Patente, Marca, Modelo"
                                    inputRef={vehiculosInputRef}
                                />
                            </TabsContent>

                            <TabsContent value="entidades">
                                <UploadSection
                                    type="entidades"
                                    icon={Building}
                                    title="Entidades (Empresas)"
                                    description="RUT, Razón Social"
                                    inputRef={entidadesInputRef}
                                />
                            </TabsContent>

                            <TabsContent value="personas">
                                <UploadSection
                                    type="personas"
                                    icon={Users}
                                    title="Personas"
                                    description="RUT, Nombre, Empresa Vinculada"
                                    inputRef={personasInputRef}
                                />
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>

                {/* Instructions */}
                <Card className="border-2">
                    <CardHeader className="card-industrial-header">
                        <CardTitle>Instrucciones</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <ul className="space-y-3 text-sm">
                            <li className="flex items-start gap-2">
                                <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">1</span>
                                <span>Descargue la plantilla correspondiente al tipo de datos</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">2</span>
                                <span>Complete los datos respetando el formato de cada columna</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">3</span>
                                <span>Guarde el archivo en formato CSV (separado por comas)</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">4</span>
                                <span>Suba el archivo usando el área de carga</span>
                            </li>
                        </ul>
                        <div className="mt-4 p-3 bg-muted/50 rounded-sm">
                            <p className="text-xs text-muted-foreground">
                                <strong>Nota:</strong> Los registros existentes con el mismo identificador (Patente, RUT) serán actualizados.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
